/**
 * Which participant should the dashboard open on?
 *
 * Extracted from `Dashboard.tsx` because the rule stopped being a one-liner:
 * it has three tiers and two failure modes that look identical on screen (a
 * blank right pane), so it is worth testing on its own rather than through a
 * full page render.
 *
 * The history matters, because each tier is scar tissue:
 *
 *   1. It once landed on `roster[0]`, which was usually a participant with no
 *      recordings at all — an empty timeline on every load.
 *   2. So it moved to "the participant with the most recordings", which does
 *      guarantee data. But that never ages: the dashboard opened on the same
 *      person every day no matter who had recorded since, and was reported as
 *      "it always shows the same weeks-old session".
 *   3. Recency alone is not the answer either. A recording that is still in
 *      progress, or whose file has not arrived yet, carries no scores and no
 *      trace — landing there reproduces (1).
 *
 * So: the most recent recording whose file has LANDED, falling back to the
 * most recent of any state, falling back to the most recordings. A tenant
 * midway through its very first capture still lands somewhere sensible.
 */

/** The fields this rule needs. Deliberately minimal so callers can pass their
 *  own row shape without adapting it. */
export interface RankableSession {
  participant_user_id: string | null;
  started_at: string;
  /** Non-null once the recording's file is in storage. The same signal the
   *  download and replay affordances gate on, and the nearest thing the
   *  client has to "this one has something to show". */
  s3_key: string | null;
}

export interface RankableParticipant {
  id: string;
  is_active: boolean;
}

/**
 * `undefined` means "not knowable yet" — sessions load after participants, and
 * a caller must not lock onto anyone while that is true. `null` means "loaded,
 * and nobody qualifies".
 */
export function pickDefaultParticipant(
  sessions: readonly RankableSession[] | undefined | null,
  participants: readonly RankableParticipant[] | undefined | null,
): string | null | undefined {
  if (!sessions || !participants) return undefined;

  const activeIds = new Set(
    participants.filter((p) => p.is_active).map((p) => p.id),
  );
  const mine = sessions.filter(
    (s) => s.participant_user_id && activeIds.has(s.participant_user_id),
  );
  if (mine.length === 0) return null;

  const newestOwner = (rows: readonly RankableSession[]): string | null => {
    let best: string | null = null;
    let bestAt = -Infinity;
    for (const s of rows) {
      const at = new Date(s.started_at).getTime();
      // A row with an unparseable timestamp is skipped rather than treated as
      // epoch 0 — otherwise one bad row would win the "oldest" comparisons and
      // lose every "newest" one, silently.
      if (Number.isNaN(at) || at <= bestAt) continue;
      bestAt = at;
      best = s.participant_user_id;
    }
    return best;
  };

  const landed = newestOwner(mine.filter((s) => s.s3_key));
  if (landed) return landed;

  const anyRecent = newestOwner(mine);
  if (anyRecent) return anyRecent;

  const counts = new Map<string, number>();
  for (const s of mine) {
    const pid = s.participant_user_id as string;
    counts.set(pid, (counts.get(pid) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestN = 0;
  for (const [id, n] of counts) {
    if (n > bestN) {
      best = id;
      bestN = n;
    }
  }
  return best;
}
